import {
  IngredientDto,
  ProductDto,
  ProductSizeDto,
  S4DIngredientDto,
  S4DOptionDto,
  S4DProductDto,
} from '@kotipizzagroup/kotipizza-products-api-client';
import {
  baseDoughCategoryId,
  doughOptionProductCategoryIds,
  ingredientCategoriesOutsideS4DRecipes,
  isLive,
  productCategoriesWithS4DRecipes,
  topSauceProductCategoryIds,
} from '../../../shared/utils/productAndIngredientUtils';
import { Commerce } from '@kotipizzagroup/kotipizza-shared-types';

export enum S4DProductStatus {
  Ok = 'ok',
  DataDiffers = 'dataDiffers',
  LinkBroken = 'linkBroken',
  NotCompared = 'notCompared',
}

export enum S4DIssueKind {
  ProductMissing = 'productMissing',
  ProductInactive = 'productInactive',
  ProductLeftover = 'productLeftover',
  ProductOnlyInS4D = 'productOnlyInS4D',
  NameDiffers = 'nameDiffers',
  PriceMissing = 'priceMissing',
  OptionMissing = 'optionMissing',
  OptionOnlyInS4D = 'optionOnlyInS4D',
  ToppingMissing = 'toppingMissing',
  ToppingCountDiffers = 'toppingCountDiffers',
  ToppingOnlyInS4D = 'toppingOnlyInS4D',
  PriceDiffers = 'priceDiffers',
}

const blockingKinds: Record<S4DIssueKind, boolean> = {
  [S4DIssueKind.ProductMissing]: true,
  [S4DIssueKind.ProductInactive]: true,
  [S4DIssueKind.ProductLeftover]: false,
  [S4DIssueKind.ProductOnlyInS4D]: false,
  [S4DIssueKind.NameDiffers]: false,
  [S4DIssueKind.PriceMissing]: true,
  [S4DIssueKind.OptionMissing]: true,
  [S4DIssueKind.OptionOnlyInS4D]: false,
  [S4DIssueKind.ToppingMissing]: false,
  [S4DIssueKind.ToppingCountDiffers]: false,
  [S4DIssueKind.ToppingOnlyInS4D]: false,
  [S4DIssueKind.PriceDiffers]: false,
};

export const isBlockingS4DIssueKind = (kind: S4DIssueKind): boolean => blockingKinds[kind];

export const getS4DStatus = (kinds: S4DIssueKind[]): S4DProductStatus =>
  kinds.some(isBlockingS4DIssueKind)
    ? S4DProductStatus.LinkBroken
    : kinds.length > 0
    ? S4DProductStatus.DataDiffers
    : S4DProductStatus.Ok;

export const belongsInS4DRecipe = (
  ingredientCategoryId: number | null | undefined,
  productCategoryId: number
): boolean => {
  if (ingredientCategoryId === undefined || ingredientCategoryId === null) return true;

  if (ingredientCategoriesOutsideS4DRecipes.includes(ingredientCategoryId)) return false;

  return !(
    ingredientCategoryId === Commerce.IngredientCategory.SAUCE &&
    topSauceProductCategoryIds.includes(productCategoryId)
  );
};

type S4DProductContext = {
  s4dProductId: number;
};

export type S4DToppingRef = {
  s4dId: number;
  name: string;
  ingredientId?: number | null;
  isDuplicate: boolean;
};

export type ToppingCountMismatch = S4DProductContext & {
  ingredientId: number;
  recipeQuantity: number;
  s4dCount: number;
  s4dToppings: S4DToppingRef[];
};

export type MissingTopping = S4DProductContext & {
  ingredientId: number;
  recipeQuantity: number;
  s4dToppings: S4DToppingRef[];
};

export type ExtraS4DTopping = S4DProductContext & {
  topping: S4DIngredientDto;
  s4dToppings: S4DToppingRef[];
  /** Set when only some of the compared S4D products have this topping. */
  s4dProductName?: string;
};

export type NameMismatch = S4DProductContext & {
  s4dName: string;
};

export type S4DProductRef = S4DProductContext & {
  name: string;
};

export type ExtraOption = S4DProductContext & {
  webCode: string;
  productSizeId: number;
  doughId: number;
};

export type MissingOption = {
  webCode: string;
  productSizeId: number;
  doughId: number;
  s4dProductId?: number;
};

export type PriceMismatch = S4DProductContext & {
  productSizeId: number;
  optionWebCode: string | null | undefined;
  ourPrice?: number;
  s4dDeliveryPrice?: number;
  s4dPickupPrice?: number;
  pickupPriceMissing?: boolean;
};

const getDuplicateNumber = (topping: S4DIngredientDto): number => Number(topping.webCode?.split('_')[1]) || 0;

const byToppingCopyOrder = (first: S4DIngredientDto, second: S4DIngredientDto): number =>
  Number(!first.isActive) - Number(!second.isActive) ||
  Number(first.isDuplicateTopping) - Number(second.isDuplicateTopping) ||
  getDuplicateNumber(first) - getDuplicateNumber(second) ||
  first.s4dId - second.s4dId;

const toToppingRefs = (toppings: S4DIngredientDto[]): S4DToppingRef[] =>
  toppings.map((topping) => ({
    s4dId: topping.s4dId,
    name: topping.name,
    ingredientId: topping.ingredientId,
    isDuplicate: Boolean(topping.isDuplicateTopping),
  }));

const normalizeName = (name: string | null | undefined): string => (name || '').replace(/\s+/g, ' ').trim();

export const namesDiffer = (ourName: string | null | undefined, s4dName: string | null | undefined): boolean => {
  const ours = normalizeName(ourName);
  const theirs = normalizeName(s4dName);

  return theirs !== ours && theirs.replace(/\s+Large$/i, '') !== ours;
};

export const pricesDiffer = (first: number, second: number): boolean =>
  Math.round(first * 100) !== Math.round(second * 100);

export type S4DProductComparison = {
  status: S4DProductStatus;
  s4dProducts: S4DProductDto[];
  hasMatchingWebCode: boolean;
  hasPriceOption: boolean;
  nameMismatches: NameMismatch[];
  inactiveS4DProducts: S4DProductRef[];
  hasNoActiveS4DProduct: boolean;
  missingOptions: MissingOption[];
  extraOptions: ExtraOption[];
  isRecipeCompared: boolean;
  missingToppings: MissingTopping[];
  toppingCountMismatches: ToppingCountMismatch[];
  extraS4DToppings: ExtraS4DTopping[];
  priceMismatches: PriceMismatch[];
};

type ComparisonInput = {
  product: ProductDto;
  s4dProducts: S4DProductDto[];
  s4dIngredients: S4DIngredientDto[];
  ingredients: IngredientDto[];
  productSizes: ProductSizeDto[];
  ourPricesBySizeId?: Map<number, number>;
};

const notComparedResult = (s4dProducts: S4DProductDto[]): S4DProductComparison => ({
  status: S4DProductStatus.NotCompared,
  s4dProducts,
  hasMatchingWebCode: s4dProducts.length > 0,
  hasPriceOption: true,
  nameMismatches: [],
  inactiveS4DProducts: [],
  hasNoActiveS4DProduct: false,
  missingOptions: [],
  extraOptions: [],
  isRecipeCompared: false,
  missingToppings: [],
  toppingCountMismatches: [],
  extraS4DToppings: [],
  priceMismatches: [],
});

export const getS4DProductsForProduct = (
  s4dProducts: S4DProductDto[],
  productId: number | undefined
): S4DProductDto[] => {
  const matching = getMatchingS4DProducts(s4dProducts, productId);
  const active = matching.filter((s4dProduct) => s4dProduct.isActive);

  return active.length > 0 ? active : matching;
};

export const getMatchingS4DProducts = (s4dProducts: S4DProductDto[], productId: number | undefined): S4DProductDto[] =>
  s4dProducts.filter((s4dProduct) => s4dProduct.productId === productId);

export const getOrphanS4DProducts = (s4dProducts: S4DProductDto[], productIds: number[]): S4DProductDto[] => {
  const ours = new Set(productIds);

  return s4dProducts.filter(
    (s4dProduct) =>
      s4dProduct.isActive && Boolean(s4dProduct.webCode?.trim()) && !ours.has(s4dProduct.productId ?? -1)
  );
};

export const getDoughsInUse = (ingredients: IngredientDto[]): IngredientDto[] =>
  ingredients.filter(
    (ingredient) =>
      ingredient.ingredientCategoryId === baseDoughCategoryId && ingredient.isActive !== false && !ingredient.isArchived
  );

export type ExpectedOption = {
  productSizeId: number;
  doughId: number;
  webCode: string;
};

export const expectsDoughOptions = (
  productSizeId: number,
  productCategoryId: number,
  existingOptions: S4DOptionDto[]
): boolean => {
  const sizeOptions = existingOptions.filter((option) => option.productSizeId === productSizeId);

  if (sizeOptions.some((option) => option.doughId !== 0)) return true;
  if (sizeOptions.length > 0) return false;

  return doughOptionProductCategoryIds.includes(productCategoryId);
};

export const getExpectedOptions = (
  productSizes: ProductSizeDto[],
  ingredients: IngredientDto[],
  productCategoryId: number,
  existingOptions: S4DOptionDto[]
): ExpectedOption[] => {
  const doughs = getDoughsInUse(ingredients);

  return productSizes.flatMap((size) => {
    if (!expectsDoughOptions(size.productSizeId, productCategoryId, existingOptions)) {
      return [{ productSizeId: size.productSizeId, doughId: 0, webCode: `${size.productSizeId}_0` }];
    }

    return doughs
      .filter((dough) => dough.dosings?.map((dosing) => dosing.productSizeId).includes(size.productSizeId))
      .map((dough) => ({
        productSizeId: size.productSizeId,
        doughId: dough.ingredientId || 0,
        webCode: `${size.productSizeId}_${dough.ingredientId}`,
      }));
  });
};

export type OwnedOption = {
  option: S4DOptionDto;
  s4dProductId: number;
};

export const findOption = (
  options: S4DOptionDto[],
  productSizeId: number,
  doughId: number
): S4DOptionDto | undefined =>
  options.find((option) => option.productSizeId === productSizeId && option.doughId === doughId);

export const getPriceComparableDoughIds = (recipeIngredientIds: number[], options: S4DOptionDto[]): number[] => [
  0,
  ...recipeIngredientIds.filter((ingredientId) => options.some((option) => option.doughId === ingredientId)),
];

export const getPriceMismatches = (
  options: OwnedOption[],
  ourPricesBySizeId: Map<number, number>,
  comparableDoughIds: number[]
): PriceMismatch[] => {
  const mismatches: PriceMismatch[] = [];

  const compared = new Set<string>();

  options.forEach(({ option, s4dProductId }) => {
    if (!comparableDoughIds.includes(option.doughId)) return;

    const optionKey = `${s4dProductId}-${option.productSizeId}-${option.doughId}`;
    if (compared.has(optionKey)) return;
    compared.add(optionKey);

    const ourPrice = ourPricesBySizeId.get(option.productSizeId);
    const pickupPrice = option.pricePickupDefault;
    const pickupPriceMissing = pickupPrice === undefined || pickupPrice === null;

    const deliveryDiffers = ourPrice !== undefined && pricesDiffer(ourPrice, option.price);
    const pickupDiffers = !pickupPriceMissing && ourPrice !== undefined && pricesDiffer(ourPrice, pickupPrice);

    if (!deliveryDiffers && !pickupDiffers && !pickupPriceMissing) return;

    mismatches.push({
      s4dProductId,
      productSizeId: option.productSizeId,
      optionWebCode: option.webCode,
      ourPrice,
      s4dDeliveryPrice: deliveryDiffers ? option.price : undefined,
      s4dPickupPrice: pickupDiffers ? pickupPrice : undefined,
      pickupPriceMissing: pickupPriceMissing || undefined,
    });
  });

  return mismatches;
};

export const compareProductToS4D = (input: ComparisonInput): S4DProductComparison => {
  const { product, s4dProducts, s4dIngredients, ingredients, productSizes, ourPricesBySizeId } = input;
  const productId = product.productId;

  const allMatchingS4DProducts = getMatchingS4DProducts(s4dProducts, productId);
  const matchingS4DProducts = getS4DProductsForProduct(s4dProducts, productId);

  if (product.isArchived) {
    return notComparedResult(matchingS4DProducts);
  }

  const hasMatchingWebCode = matchingS4DProducts.some(
    (s4dProduct) => s4dProduct.webCode?.trim() === String(productId)
  );

  if (!hasMatchingWebCode) {
    return {
      status: getS4DStatus([S4DIssueKind.ProductMissing]),
      s4dProducts: matchingS4DProducts,
      hasMatchingWebCode,
      hasPriceOption: false,
      nameMismatches: [],
      inactiveS4DProducts: [],
      hasNoActiveS4DProduct: false,
      missingOptions: [],
      extraOptions: [],
      isRecipeCompared: false,
      missingToppings: [],
      toppingCountMismatches: [],
      extraS4DToppings: [],
      priceMismatches: [],
    };
  }

  const ownedOptions: OwnedOption[] = matchingS4DProducts.flatMap((s4dProduct) =>
    s4dProduct.options.map((option) => ({ option, s4dProductId: s4dProduct.s4dId }))
  );
  const options = ownedOptions.map((owned) => owned.option);

  const expectedOptions = getExpectedOptions(productSizes, ingredients, product.productCategoryId, options);

  const missingOptions: MissingOption[] = expectedOptions
    .filter((expected) => !findOption(options, expected.productSizeId, expected.doughId))
    .map((expected) => ({
      ...expected,
      s4dProductId: ownedOptions.find((owned) => owned.option.productSizeId === expected.productSizeId)?.s4dProductId,
    }));

  const isDoughInUse = (doughId: number): boolean => {
    if (doughId === 0) return true;

    const ourDough = ingredients.find((ingredient) => ingredient.ingredientId === doughId);
    if (ourDough && (ourDough.isActive === false || ourDough.isArchived)) return false;

    const s4dDough = s4dIngredients.find((topping) => topping.ingredientId === doughId);

    return !(s4dDough && !s4dDough.isActive);
  };

  const extraOptions: ExtraOption[] = [];
  const reportedExtras = new Set<string>();

  ownedOptions.forEach(({ option, s4dProductId }) => {
    const isExpected = expectedOptions.some(
      (expected) => expected.productSizeId === option.productSizeId && expected.doughId === option.doughId
    );
    if (isExpected || !isDoughInUse(option.doughId)) return;

    const key = `${s4dProductId}-${option.productSizeId}-${option.doughId}`;
    if (reportedExtras.has(key)) return;
    reportedExtras.add(key);

    extraOptions.push({
      s4dProductId,
      productSizeId: option.productSizeId,
      doughId: option.doughId,
      webCode: option.webCode?.trim() || `${option.productSizeId}_${option.doughId}`,
    });
  });

  const hasPriceOption = options.length > 0;

  const inactiveMatches = isLive(product) ? allMatchingS4DProducts.filter((s4dProduct) => !s4dProduct.isActive) : [];

  const hasNoActiveS4DProduct =
    allMatchingS4DProducts.length > 0 && inactiveMatches.length === allMatchingS4DProducts.length;

  // S4D can have a product of its own for a size of ours, e.g. California Monster Large. When we
  // don't sell that size, its S4D product is rightly inactive.
  const soldSizeIds = productSizes.map((size) => size.productSizeId);
  const isForSoldSize = (s4dProduct: S4DProductDto): boolean =>
    soldSizeIds.length === 0 ||
    s4dProduct.options.length === 0 ||
    s4dProduct.options.some((option) => soldSizeIds.includes(option.productSizeId));

  const inactiveS4DProducts: S4DProductRef[] = (
    hasNoActiveS4DProduct ? inactiveMatches : inactiveMatches.filter(isForSoldSize)
  ).map((s4dProduct) => ({
    s4dProductId: s4dProduct.s4dId,
    name: s4dProduct.name,
  }));

  const nameMismatches: NameMismatch[] = matchingS4DProducts
    .filter((s4dProduct) => namesDiffer(product.name, s4dProduct.name))
    .map((s4dProduct) => ({ s4dProductId: s4dProduct.s4dId, s4dName: s4dProduct.name }));

  const ingredientCategories = new Map(
    ingredients.map((ingredient) => [ingredient.ingredientId, ingredient.ingredientCategoryId])
  );

  const s4dToppingCopies = new Map<number, S4DIngredientDto[]>();
  s4dIngredients.forEach((topping) => {
    if (!topping.ingredientId) return;
    const copies = s4dToppingCopies.get(topping.ingredientId);
    if (copies) copies.push(topping);
    else s4dToppingCopies.set(topping.ingredientId, [topping]);
  });
  s4dToppingCopies.forEach((copies) => copies.sort(byToppingCopyOrder));

  const fullRecipe = product.ingredients || [];

  const comparesRecipe = productCategoriesWithS4DRecipes.includes(product.productCategoryId);
  const recipe = comparesRecipe
    ? fullRecipe.filter((row) =>
        belongsInS4DRecipe(ingredientCategories.get(row.ingredientId), product.productCategoryId)
      )
    : [];

  const missingToppings: MissingTopping[] = [];
  const toppingCountMismatches: ToppingCountMismatch[] = [];
  const foundExtras: { key: string; s4dProductName: string; extra: ExtraS4DTopping }[] = [];

  let comparedRecipes = 0;

  matchingS4DProducts.forEach((s4dProduct) => {
    const s4dProductId = s4dProduct.s4dId;
    const s4dProductToppings = s4dIngredients.filter((ingredient) => s4dProduct.toppingIds.includes(ingredient.s4dId));
    const s4dProductDoughIds = s4dProduct.options.map((option) => option.doughId);

    if (s4dProductToppings.length === 0 && s4dProduct.canChangeToppings === false) return;

    comparedRecipes += 1;

    recipe.forEach((row) => {
      if (s4dProductDoughIds.includes(row.ingredientId)) return;

      const copies = s4dToppingCopies.get(row.ingredientId) || [];
      const inS4DRecipe = copies.filter((topping) => s4dProduct.toppingIds.includes(topping.s4dId));
      const s4dCount = inS4DRecipe.length;

      if (s4dCount === row.quantity) return;

      const s4dToppings =
        s4dCount < row.quantity
          ? toToppingRefs(
              copies.filter((topping) => !inS4DRecipe.includes(topping)).slice(0, row.quantity - s4dCount)
            )
          : toToppingRefs(inS4DRecipe.slice(row.quantity));

      if (s4dCount === 0) {
        missingToppings.push({
          ingredientId: row.ingredientId,
          recipeQuantity: row.quantity,
          s4dProductId,
          s4dToppings,
        });
      } else {
        toppingCountMismatches.push({
          ingredientId: row.ingredientId,
          recipeQuantity: row.quantity,
          s4dCount,
          s4dProductId,
          s4dToppings,
        });
      }
    });

    if (!comparesRecipe) return;

    const extraCopies = new Map<string, S4DIngredientDto[]>();
    s4dProductToppings
      .filter((topping) => !recipe.some((row) => row.ingredientId === topping.ingredientId))
      .forEach((topping) => {
        const key = topping.ingredientId ? `ingredient-${topping.ingredientId}` : `topping-${topping.s4dId}`;
        const copies = extraCopies.get(key);
        if (copies) copies.push(topping);
        else extraCopies.set(key, [topping]);
      });

    extraCopies.forEach((copies, key) => {
      copies.sort(byToppingCopyOrder);
      foundExtras.push({
        key,
        s4dProductName: s4dProduct.name,
        extra: { topping: copies[0], s4dToppings: toToppingRefs(copies), s4dProductId },
      });
    });
  });

  const extraS4DToppings: ExtraS4DTopping[] = foundExtras.map(({ key, s4dProductName, extra }) =>
    foundExtras.filter((found) => found.key === key).length < comparedRecipes ? { ...extra, s4dProductName } : extra
  );

  const priceMismatches = ourPricesBySizeId
    ? getPriceMismatches(
        ownedOptions,
        ourPricesBySizeId,
        getPriceComparableDoughIds(
          fullRecipe.map((row) => row.ingredientId),
          options
        )
      )
    : [];

  const issueKinds: S4DIssueKind[] = [
    ...(hasNoActiveS4DProduct
      ? [S4DIssueKind.ProductInactive]
      : inactiveS4DProducts.map(() => S4DIssueKind.ProductLeftover)),
    ...nameMismatches.map(() => S4DIssueKind.NameDiffers),
    ...(hasPriceOption ? [] : [S4DIssueKind.PriceMissing]),
    ...missingOptions.map(() => S4DIssueKind.OptionMissing),
    ...extraOptions.map(() => S4DIssueKind.OptionOnlyInS4D),
    ...missingToppings.map(() => S4DIssueKind.ToppingMissing),
    ...toppingCountMismatches.map(() => S4DIssueKind.ToppingCountDiffers),
    ...extraS4DToppings.map(() => S4DIssueKind.ToppingOnlyInS4D),
    ...priceMismatches.map(() => S4DIssueKind.PriceDiffers),
  ];

  return {
    status: getS4DStatus(issueKinds),
    s4dProducts: matchingS4DProducts,
    hasMatchingWebCode,
    hasPriceOption,
    nameMismatches,
    inactiveS4DProducts,
    hasNoActiveS4DProduct,
    missingOptions,
    extraOptions,
    isRecipeCompared: recipe.length > 0 && comparedRecipes > 0,
    missingToppings,
    toppingCountMismatches,
    extraS4DToppings,
    priceMismatches,
  };
};
